import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { CheckIcon, ChevronDownIcon } from "lucide-react";
import { priceBuckets } from "../../data/products";
import type { ShopFilters } from "../../hooks/useShopFilters";
import type { Category } from "../../types/product";
import { colorSwatches } from "../../utils/format";
import { ease } from "../../utils/styles";
interface FilterPanelProps {
  filters: ShopFilters;
  options: {
    categories: {
      value: Category;
      count: number;
    }[];
    types: {
      value: string;
      count: number;
    }[];
    colors: string[];
  };
  toggle: (key: "categories" | "types" | "colors" | "prices", value: string) => void;
  setPersonalizable: (value: boolean) => void;
}
export function FilterPanel({
  filters,
  options,
  toggle,
  setPersonalizable
}: FilterPanelProps) {
  return <div className="divide-y divide-line" data-id="element-425" data-mp="249x3stoiyer5">
      <Group title="Producttype" data-id="element-426" data-mp="29285a0ywy8lt">
        {options.types.map(o => <CheckRow key={o.value} label={o.value} count={o.count} checked={filters.types.includes(o.value)} onChange={() => toggle("types", o.value)} data-id="element-427" data-mp="294b1j2qnsxyz" />)}
      </Group>
      <Group title="Prijs" data-id="element-428" data-mp="2ougwcn9yrxq5">
        {priceBuckets.map(b => <CheckRow key={b.id} label={b.label} checked={filters.prices.includes(b.id)} onChange={() => toggle("prices", b.id)} data-id="element-429" data-mp="32l3tib02tfk4" />)}
      </Group>
      <Group title="Kleur" data-id="element-430" data-mp="2xskc12y9q0a1">
        <div className="grid grid-cols-4 gap-3 pt-1" data-id="element-431" data-mp="3cirqz8p2h15z">
          {options.colors.map(c => {
          const active = filters.colors.includes(c);
          return <button key={c} type="button" onClick={() => toggle("colors", c)} aria-pressed={active} className="flex flex-col items-center gap-1.5 text-[11px] text-ink/80" data-id="element-432" data-mp="37lrikr4qvmbp">
                <span className={`h-8 w-8 rounded-full transition-shadow duration-150 ${active ? "ring-2 ring-ink ring-offset-2" : "ring-1 ring-ink/10"}`} style={{
              backgroundColor: colorSwatches[c]
            }} data-id="element-433" data-mp="3hj5ucdfq3mxo" />
                <span className="truncate" data-id="element-434" data-mp="1n5eeuxapq8ub">{c}</span>
              </button>;
        })}
        </div>
      </Group>
      <div className="flex items-center justify-between py-5" data-id="element-435" data-mp="3may0ul1v7phd">
        <span id="pers-label" className="text-sm font-medium text-ink" data-id="element-436" data-mp="3r8g1zp9n50et">
          Personaliseerbaar
        </span>
        <button type="button" role="switch" aria-checked={filters.personalizable} aria-labelledby="pers-label" onClick={() => setPersonalizable(!filters.personalizable)} className={`relative h-6 w-11 rounded-full transition-colors duration-200 ${filters.personalizable ? "bg-ink" : "bg-stone"}`} data-id="element-437" data-mp="252sgsrcau">
          <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow-sm transition-transform duration-200 ease-out ${filters.personalizable ? "translate-x-[22px]" : "translate-x-0.5"}`} data-id="element-438" data-mp="irla14udt0fi" />
        </button>
      </div>
    </div>;
}
function Group({
  title,
  children
}: {
  title: string;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(true);
  return <div className="py-5 first:pt-0" data-id="element-439" data-mp="2ha6ihw1jfx09">
      <button type="button" onClick={() => setOpen(o => !o)} aria-expanded={open} className="flex w-full items-center justify-between text-sm font-medium text-ink" data-id="element-440" data-mp="2hb86t27kefzx">
        {title}
        <ChevronDownIcon className={`h-4 w-4 text-muted transition-transform duration-200 ease-out ${open ? "rotate-180" : ""}`} data-id="element-441" data-mp="28a1kudm5ccza" />
      </button>
      <AnimatePresence initial={false} data-id="element-442" data-mp="23c8g7p0hrr14">
        {open && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: "auto",
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease
      }} className="overflow-hidden" data-id="element-443" data-mp="30wcndttbcnyq">
            <div className="space-y-0.5 pt-3" data-id="element-444" data-mp="2r6jjzg3brf5k">{children}</div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
function CheckRow({
  label,
  count,
  checked,
  onChange
}: {
  label: string;
  count?: number;
  checked: boolean;
  onChange: () => void;
}) {
  return <label className="flex cursor-pointer items-center gap-3 py-1.5 text-sm text-ink/80 transition-colors duration-150 hover:text-ink" data-id="element-445" data-mp="3ex5h4cbsjf0s">
      <input type="checkbox" checked={checked} onChange={onChange} className="peer sr-only" data-id="element-446" data-mp="2ze47s79pzugm" />
      <span className={`flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-[5px] transition-colors duration-150 peer-focus-visible:ring-2 peer-focus-visible:ring-ink ${checked ? "bg-ink text-paper" : "bg-paper ring-1 ring-inset ring-ink/25"}`} data-id="element-447" data-mp="33ikci57lkvu9">
        {checked && <CheckIcon className="h-3 w-3" strokeWidth={3} data-id="element-448" data-mp="3i6ycm8dmq674" />}
      </span>
      <span className="flex-1" data-id="element-449" data-mp="3n4qhs43abot9">{label}</span>
      {count !== undefined && <span className="text-xs text-muted tabular-nums" data-id="element-450" data-mp="3n72uo2u6r8zg">{count}</span>}
    </label>;
}